import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Plus, Users, Zap, Trash2, ChevronDown } from 'lucide-react';
import api from '@/lib/api';
import { formatDate } from '@/lib/format';
import { Card, Empty } from '@/components/shared';

const TYPES = [['campaign', 'Kampanya'], ['event', 'Etkinlik'], ['notification', 'Bildirim']];
const TIERS = ['', 'Bronze', 'Silver', 'Gold', 'Diamond'];

export default function AdminCampaigns() {
  const [list, setList] = useState([]);
  const [form, setForm] = useState({ type: 'campaign', title: '', body: '', targetTier: '', image: '', rsvpEnabled: false, eventDate: '', publishAt: '' });
  const [busy, setBusy] = useState(false);
  const [attendees, setAttendees] = useState({});

  const [camps, setCamps] = useState([]);
  const [cform, setCform] = useState({ name: '', multiplier: 2, category: '', startDate: '', endDate: '', targetTier: '' });
  const [cbusy, setCbusy] = useState(false);

  const load = () => api.get('/admin/notifications').then((r) => setList(r.data)).catch(() => {});
  const loadCamps = () => api.get('/admin/commission-campaigns').then((r) => setCamps(r.data)).catch(() => {});
  useEffect(() => { load(); loadCamps(); }, []);

  const submit = async () => {
    if (!form.title.trim() || !form.body.trim()) { toast.error('Başlık ve içerik gerekli'); return; }
    setBusy(true);
    try {
      await api.post('/admin/notifications', {
        ...form, targetTier: form.targetTier || null, image: form.image || null,
        eventDate: form.eventDate || null, publishAt: form.publishAt || null,
      });
      toast.success(form.publishAt ? 'Yayın planlandı' : 'Yayınlandı');
      setForm({ type: 'campaign', title: '', body: '', targetTier: '', image: '', rsvpEnabled: false, eventDate: '', publishAt: '' });
      load();
    } catch { toast.error('Yayınlanamadı'); }
    finally { setBusy(false); }
  };

  const viewAttendees = async (id) => {
    if (attendees[id]) { setAttendees({ ...attendees, [id]: null }); return; }
    try {
      const r = await api.get(`/admin/events/${id}/rsvps`);
      setAttendees({ ...attendees, [id]: r.data });
    } catch { toast.error('Katılımcılar alınamadı'); }
  };

  const submitCamp = async () => {
    if (!cform.name.trim() || !cform.startDate || !cform.endDate) { toast.error('İsim ve tarih aralığı gerekli'); return; }
    setCbusy(true);
    try {
      await api.post('/admin/commission-campaigns', {
        name: cform.name, multiplier: parseFloat(cform.multiplier),
        category: cform.category || null, startDate: cform.startDate, endDate: cform.endDate,
        targetTier: cform.targetTier || null,
      });
      toast.success('Komisyon kampanyası oluşturuldu');
      setCform({ name: '', multiplier: 2, category: '', startDate: '', endDate: '', targetTier: '' });
      loadCamps();
    } catch { toast.error('Oluşturulamadı'); }
    finally { setCbusy(false); }
  };

  const deleteCamp = async (id) => {
    try { await api.delete(`/admin/commission-campaigns/${id}`); toast.success('Silindi'); loadCamps(); }
    catch { toast.error('Silinemedi'); }
  };

  return (
    <div className="fade-up max-w-3xl" data-testid="admin-campaigns">
      <h1 className="font-serif text-3xl text-navy mb-1">Kampanyalar & Etkinlikler</h1>
      <p className="text-navy/50 text-sm mb-6">Etkinlik, kampanya, bildirim yayınla ve çarpanlı komisyon kampanyaları oluştur</p>

      {/* Content publisher */}
      <Card className="p-6 mb-8">
        <h2 className="font-serif text-lg text-navy mb-4">İçerik Yayınla</h2>
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            <label className="text-xs text-navy/50 block mb-1">Tür</label>
            <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} data-testid="campaign-type" className="w-full bg-cream border border-navy/15 rounded-md px-3 py-2 text-sm focus:outline-none">
              {TYPES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-navy/50 block mb-1">Hedef seviye (opsiyonel)</label>
            <select value={form.targetTier} onChange={(e) => setForm({ ...form, targetTier: e.target.value })} data-testid="campaign-tier" className="w-full bg-cream border border-navy/15 rounded-md px-3 py-2 text-sm focus:outline-none">
              {TIERS.map((t) => <option key={t} value={t}>{t || 'Tümü'}</option>)}
            </select>
          </div>
        </div>
        <div className="mb-4">
          <label className="text-xs text-navy/50 block mb-1">Başlık</label>
          <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="campaign-title" className="w-full bg-cream border border-navy/15 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-gold" />
        </div>
        <div className="mb-4">
          <label className="text-xs text-navy/50 block mb-1">İçerik</label>
          <textarea value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} rows={3} data-testid="campaign-body" className="w-full bg-cream border border-navy/15 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-gold resize-none" />
        </div>
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            <label className="text-xs text-navy/50 block mb-1">Görsel URL (opsiyonel)</label>
            <input value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} data-testid="campaign-image" className="w-full bg-cream border border-navy/15 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-gold" />
          </div>
          <div>
            <label className="text-xs text-navy/50 block mb-1">Etkinlik tarihi (opsiyonel)</label>
            <input type="date" value={form.eventDate} onChange={(e) => setForm({ ...form, eventDate: e.target.value })} data-testid="campaign-event-date" className="w-full bg-cream border border-navy/15 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-gold" />
          </div>
        </div>
        <label className="flex items-center gap-2 mb-4 cursor-pointer">
          <input type="checkbox" checked={form.rsvpEnabled} onChange={(e) => setForm({ ...form, rsvpEnabled: e.target.checked })} data-testid="campaign-rsvp-toggle" className="w-4 h-4 accent-ink" />
          <span className="text-sm text-navy">Katılım (RSVP) açık — "Katılacağım" butonu göster</span>
        </label>
        <div className="mb-4">
          <label className="text-xs text-navy/50 block mb-1">Zamanlı yayın (opsiyonel — ileri tarih)</label>
          <input type="datetime-local" min={new Date().toISOString().slice(0, 16)} value={form.publishAt} onChange={(e) => setForm({ ...form, publishAt: e.target.value })} data-testid="campaign-publish-at" className="w-full bg-cream border border-navy/15 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-gold" />
          <p className="text-xs text-navy/40 mt-1">Boş bırakılırsa hemen yayınlanır. İleri tarih girilirse o tarihte otomatik yayınlanır.</p>
        </div>
        <button onClick={submit} disabled={busy} data-testid="campaign-submit" className="inline-flex items-center gap-2 bg-navy text-cream rounded-md px-5 py-2.5 text-sm font-medium active:scale-95 transition-transform disabled:opacity-50">
          <Plus strokeWidth={1.5} className="w-4 h-4" /> {busy ? 'Kaydediliyor...' : (form.publishAt ? 'Planla' : 'Yayınla')}
        </button>
      </Card>

      {/* Commission multiplier campaigns */}
      <Card className="p-6 mb-8" data-testid="commission-campaigns">
        <div className="flex items-center gap-2 mb-1">
          <Zap strokeWidth={1.5} className="w-5 h-5 text-gold" />
          <h2 className="font-serif text-lg text-navy">Komisyon Kampanyaları (Çarpan)</h2>
        </div>
        <p className="text-xs text-navy/50 mb-4">Belirli tarih aralığında komisyonu çarpanla artırır (ör. çifte komisyon). Opsiyonel kategori ile sınırlandırılabilir.</p>
        <div className="grid grid-cols-2 gap-4 mb-3">
          <div>
            <label className="text-xs text-navy/50 block mb-1">Kampanya adı</label>
            <input value={cform.name} onChange={(e) => setCform({ ...cform, name: e.target.value })} data-testid="cc-name" className="w-full bg-cream border border-navy/15 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-gold" />
          </div>
          <div>
            <label className="text-xs text-navy/50 block mb-1">Çarpan (ör. 2 = 2x)</label>
            <input type="number" step="0.1" value={cform.multiplier} onChange={(e) => setCform({ ...cform, multiplier: e.target.value })} data-testid="cc-multiplier" className="w-full bg-cream border border-navy/15 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-gold" />
          </div>
          <div>
            <label className="text-xs text-navy/50 block mb-1">Kategori (opsiyonel)</label>
            <input value={cform.category} onChange={(e) => setCform({ ...cform, category: e.target.value })} placeholder="ör. Aydınlatma" data-testid="cc-category" className="w-full bg-cream border border-navy/15 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-gold" />
          </div>
          <div>
            <label className="text-xs text-navy/50 block mb-1">Hedef seviye (opsiyonel)</label>
            <select value={cform.targetTier} onChange={(e) => setCform({ ...cform, targetTier: e.target.value })} data-testid="cc-tier" className="w-full bg-cream border border-navy/15 rounded-md px-3 py-2 text-sm focus:outline-none">
              {TIERS.map((t) => <option key={t} value={t}>{t || 'Tümü'}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-navy/50 block mb-1">Başlangıç</label>
            <input type="date" value={cform.startDate} onChange={(e) => setCform({ ...cform, startDate: e.target.value })} data-testid="cc-start" className="w-full bg-cream border border-navy/15 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-gold" />
          </div>
          <div>
            <label className="text-xs text-navy/50 block mb-1">Bitiş</label>
            <input type="date" value={cform.endDate} onChange={(e) => setCform({ ...cform, endDate: e.target.value })} data-testid="cc-end" className="w-full bg-cream border border-navy/15 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-gold" />
          </div>
        </div>
        <button onClick={submitCamp} disabled={cbusy} data-testid="cc-submit" className="inline-flex items-center gap-2 bg-navy text-cream rounded-md px-5 py-2.5 text-sm font-medium active:scale-95 transition-transform disabled:opacity-50">
          <Plus strokeWidth={1.5} className="w-4 h-4" /> {cbusy ? 'Oluşturuluyor...' : 'Kampanya Oluştur'}
        </button>

        <div className="mt-5 space-y-2" data-testid="cc-list">
          {camps.length === 0 ? <p className="text-sm text-navy/40">Kampanya yok.</p> : camps.map((c) => (
            <div key={c.id} className="flex items-center justify-between bg-cream border border-navy/10 rounded-md p-3">
              <div>
                <p className="text-sm text-navy font-medium flex items-center gap-2">
                  {c.name}
                  <span className="text-xs px-2 py-0.5 rounded-full bg-gold/15 text-gold">{c.multiplier}x</span>
                  {c.category && <span className="text-xs px-2 py-0.5 rounded-full bg-navy/10 text-navy/70">{c.category}</span>}
                  {c.active ? <span className="text-xs px-2 py-0.5 rounded-full bg-pos/10 text-greenx">Aktif</span> : <span className="text-xs px-2 py-0.5 rounded-full bg-cream2 text-navy/50">Pasif</span>}
                </p>
                <p className="text-xs text-navy/50 mt-0.5">{formatDate(c.startDate)} — {formatDate(c.endDate)}{c.targetTier ? ` · ${c.targetTier}` : ''}</p>
              </div>
              <button onClick={() => deleteCamp(c.id)} data-testid={`cc-delete-${c.id}`} className="text-redx p-1.5 active:scale-90 transition-transform"><Trash2 strokeWidth={1.5} className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      </Card>

      <h2 className="font-serif text-lg text-navy mb-3">Yayınlananlar</h2>
      {list.length === 0 ? (
        <Empty text="Kayıt yok" />
      ) : (
        <div className="space-y-2" data-testid="campaign-list">
          {list.map((n) => (
            <Card key={n.id} className="p-4">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-navy/10 text-navy/70">{n.type}</span>
                    {n.published === false && <span className="text-[10px] px-2 py-0.5 rounded-full bg-gold/15 text-gold" data-testid={`scheduled-${n.id}`}>Zamanlı · {formatDate(n.publishAt)}</span>}
                    {n.targetTier && <span className="text-[10px] px-2 py-0.5 rounded-full bg-gold/15 text-gold">{n.targetTier}</span>}
                    {n.rsvpEnabled && <span className="text-[10px] px-2 py-0.5 rounded-full bg-pos/10 text-greenx">RSVP · {n.rsvpCount || 0}</span>}
                    <span className="text-xs text-navy/40">{formatDate(n.date)}</span>
                  </div>
                  <p className="text-sm text-navy font-medium">{n.title}</p>
                  <p className="text-xs text-navy/60 mt-0.5">{n.body}</p>
                </div>
                {n.rsvpEnabled && (
                  <button onClick={() => viewAttendees(n.id)} data-testid={`view-rsvps-${n.id}`} className="shrink-0 inline-flex items-center gap-1 text-xs border border-navy/20 text-navy px-2.5 py-1.5 rounded-md active:scale-95 transition-transform">
                    <Users strokeWidth={1.5} className="w-3.5 h-3.5" /> Katılımcılar <ChevronDown strokeWidth={1.5} className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
              {attendees[n.id] && (
                <div className="mt-3 pt-3 border-t border-navy/10" data-testid={`attendees-${n.id}`}>
                  {attendees[n.id].length === 0 ? (
                    <p className="text-xs text-navy/40">Henüz katılımcı yok.</p>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {attendees[n.id].map((a, i) => (
                        <span key={i} className="text-xs bg-cream border border-navy/10 rounded-full px-3 py-1 text-navy/70">{a.name} · {a.tier}</span>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
